import React, { useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import { GetUser } from '@customTypes/user';
import { GET_USER } from '@api/user';
import { useAppSelector } from '@store/hooks';
import { AdultLabelIcon, Coin1Icon } from 'public/assets';

import InfoPopupContainer from './container/InfoPopupContainer';
import StoryDetailButton from '@components/storyDetail/button/Button';
import { SPLoading, SPImage } from '@components/commons';
import { viewEachChapterPurchasePopup } from '@/lib/eventManager';
import useCheckAdultCertification from '@/hooks/useCheckAdultCertification';

interface IChapterPurchasePopup {
  storyName?: string;
  chapterName?: string;
  chapterId?: number;
  storyId?: number;
  chapterIndex?: number;
  lastPreviewChapterIndex?: number;
  price: number;
  isShow: boolean;
  onClose: () => void;
  chapterImage?: string;
  isWebNovel?: boolean;
  showAdultChapterIcon: boolean;
}

const ChapterPurchasePopup = ({
  storyName,
  chapterName,
  chapterId,
  storyId,
  chapterIndex,
  chapterImage,
  price,
  isShow,
  onClose,
  isWebNovel = false,
  showAdultChapterIcon,
}: IChapterPurchasePopup) => {
  const { t } = useTranslation();
  const { previousScreen } = useAppSelector((state) => state.event);
  const { data: userData } = useQuery<GetUser>(GET_USER);

  const { goToAdultChapter } = useCheckAdultCertification({
    storyId,
    chapterId,
  });

  useEffect(() => {
    isShow &&
      viewEachChapterPurchasePopup({
        storyName,
        storyId: String(storyId),
        chapterId: String(chapterId),
        chapterIndex,
        fullCoin: price,
        coin: price,
        from: previousScreen,
        chapterCoin: price,
        contentType: isWebNovel ? 'novel' : 'interactive',
      });
  }, [isShow]);

  if (!isShow) {
    return null;
  }

  if (!userData) {
    return <SPLoading />;
  }

  const handlePurchaseChapterButtonClick = () => {
    if (isWebNovel) {
      storyId &&
        chapterId &&
        goToAdultChapter({
          storyId,
          chapterId,
          price,
        });

      onClose();
      return;
    }

    storyId &&
      chapterId &&
      goToAdultChapter({
        storyId,
        chapterId,
        price,
        query: {
          prevScreen: 'story_detail',
        },
      });

    onClose();
  };

  return (
    <InfoPopupContainer
      customCSS={popupContainer}
      title={t('chat_popup_title_purchase_chapter')}
      onClose={onClose}
    >
      <div css={container}>
        <div className="content">
          <div className="summary">
            <div className="image-wrapper">
              <SPImage src={chapterImage ?? ''} width={52} height={80} />
            </div>
            <div className="title-wrapper">
              <p className="story-title">{storyName}</p>
              <h2 className="chapter-name">
                {showAdultChapterIcon && (
                  <AdultLabelIcon
                    width={16}
                    height={16}
                    className="adult-icon"
                  />
                )}
                <p>{chapterName}</p>
              </h2>
              <p className="coin-wrapper">
                <Coin1Icon width={24} height={24} />
                <span className="price">{price}</span>
              </p>
            </div>
          </div>
        </div>
        <p className="description">
          {t('webnovel_toast_title_chapter_use_coin', { value: price })}
        </p>
        <div className="button-wrapper">
          <StoryDetailButton
            isFill={true}
            position="center"
            onClick={handlePurchaseChapterButtonClick}
          >
            {t('common_screen_button_purchase')}
          </StoryDetailButton>
        </div>
      </div>
    </InfoPopupContainer>
  );
};

const popupContainer = css`
  max-width: 19.5rem;

  .popup-wrapper {
    .title {
      font-weight: 700;
      font-size: 18px;
      line-height: 22px;
      letter-spacing: -0.3px;
      color: ${theme.colors.gray900};
    }
  }
`;

const container = css`
  margin-top: 1em;

  .content {
    display: flex;

    .summary {
      display: flex;

      .image-wrapper {
        img {
          object-fit: cover;
          border-radius: 0.125rem;
        }
      }

      .title-wrapper {
        display: flex;
        flex-direction: column;
        margin-top: 0.1875rem;
        margin-left: 1em;

        .story-title {
          font: ${theme.font.body3Normal};
          color: ${theme.colors.primary500};
          line-height: 1em;
          margin-bottom: 0.125em;
        }

        .chapter-name {
          font: ${theme.font.body1Bold};
          color: ${theme.colors.gray900};
          line-height: 1.5em;
          margin-bottom: 0.5em;
          display: flex;
          align-items: flex-start;
          gap: 0.25rem;

          .adult-icon {
            margin-top: 0.1875rem;
            flex-shrink: 0;
          }
        }

        .coin-wrapper {
          display: flex;
          align-items: center;
          gap: 4px;
          color: ${theme.colors.gray900};
          font: ${theme.font.header.subhead3};

          .price {
            padding-top: 3px;
          }

          svg {
            width: 1.5rem;
            height: 1.5rem;
          }
        }

        .ad-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.5625rem;
          font-weight: 800;
          color: ${theme.colors.gray500};
          border: 0.125rem solid ${theme.colors.gray400};
          border-radius: 0.125rem;
          padding: 0.1563rem 0.25rem 0.0625rem;
          width: 1.5rem;
          height: 1.125rem;
        }
      }
    }
  }

  .description {
    font: ${theme.font.body2Normal};
    color: ${theme.colors.gray600};
    line-height: 1.25em;
    margin: 1em 0;
  }

  .button-wrapper {
    display: flex;
    padding-top: 1em;
  }
`;

export default ChapterPurchasePopup;
